import { useMemo, useState, useEffect, useRef } from "react";
import { CaretLeft, CaretRight, Bell, CheckCircle, X, Trash, Repeat, PencilSimple, GlobeHemisphereEast, AirplaneTilt } from "@phosphor-icons/react";
import { useTrainerTimezone, formatTimeInTz, shortTzLabel } from "../lib/useTrainerTimezone";

const HOUR_START = 6;   // 6am
const HOUR_END = 22;    // 10pm
const HOUR_PX = 56;     // height per hour
const SLOT_MIN = 15;    // sub-hour slot granularity
const SLOTS_PER_HOUR = 60 / SLOT_MIN;

const STATUS_STYLES = {
  scheduled: "bg-brand/25 border-brand text-[#A8B9DC]",
  completed: "bg-emerald-500/15 border-emerald-500/60 text-emerald-300",
  cancelled: "bg-zinc-500/10 border-zinc-500/40 text-zinc-400 line-through opacity-60",
  no_show: "bg-amber-500/15 border-amber-500/60 text-amber-300",
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfWeek(d) {
  // Monday-start week
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // 0..6, Mon=0
  x.setDate(x.getDate() - day);
  return x;
}
function startOfMonth(d) {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function fmtRange(daysArr) {
  if (!daysArr.length) return "";
  const first = daysArr[0];
  const last = daysArr[daysArr.length - 1];
  if (daysArr.length === 1) {
    return first.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  }
  const monShort = (d) => d.toLocaleDateString(undefined, { month: "short" });
  if (first.getMonth() === last.getMonth()) {
    return `${monShort(first)} ${first.getDate()} – ${last.getDate()}, ${last.getFullYear()}`;
  }
  return `${monShort(first)} ${first.getDate()} – ${monShort(last)} ${last.getDate()}, ${last.getFullYear()}`;
}
function fmtMonth(d) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// Iter 54 — figure out which client-away periods overlap a given day.
// Both inputs are normalised to their local midnight timestamps to avoid TZ drift.
function awayForDay(periods, day) {
  if (!periods || periods.length === 0) return [];
  const d = startOfDay(day).getTime();
  return periods.filter((p) => {
    const s = startOfDay(new Date(`${p.start_date}T00:00:00`)).getTime();
    const e = startOfDay(new Date(`${p.end_date}T00:00:00`)).getTime();
    return d >= s && d <= e;
  });
}

export default function ScheduleCalendar({ sessions, view, awayPeriods = [], onCreateAt, onAction, onMove, onResize }) {
  const [cursor, setCursor] = useState(() => new Date());
  useEffect(() => { setCursor(new Date()); }, [view]);

  if (view === "month") {
    return <MonthView cursor={cursor} setCursor={setCursor} sessions={sessions} awayPeriods={awayPeriods} onCreateAt={onCreateAt} onAction={onAction} />;
  }
  const dayCount = view === "1day" ? 1 : view === "3day" ? 3 : 7;
  return (
    <DayStripView
      cursor={cursor}
      setCursor={setCursor}
      sessions={sessions}
      awayPeriods={awayPeriods}
      onCreateAt={onCreateAt}
      onAction={onAction}
      onMove={onMove}
      onResize={onResize}
      dayCount={dayCount}
    />
  );
}

// ---------------- Day-strip View (1 / 3 / 7 day) ----------------
function DayStripView({ cursor, setCursor, sessions, awayPeriods = [], onCreateAt, onAction, onMove, onResize, dayCount }) {
  const startDay = useMemo(() => {
    if (dayCount === 7) return startOfWeek(cursor);
    if (dayCount === 1) return startOfDay(cursor);
    return startOfDay(cursor); // 3-day: cursor is the leftmost day
  }, [cursor, dayCount]);

  const days = useMemo(
    () => Array.from({ length: dayCount }, (_, i) => addDays(startDay, i)),
    [startDay, dayCount]
  );
  const hours = useMemo(() => Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i), []);
  const [active, setActive] = useState(null); // session id for popover

  // group sessions by day-key
  const byDay = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      const dt = new Date(s.start_time);
      const key = startOfDay(dt).toISOString();
      (map[key] ||= []).push(s);
    }
    return map;
  }, [sessions]);

  const today = startOfDay(new Date()).toISOString();
  const navStep = dayCount; // jump one full strip at a time
  const testId = `calendar-${dayCount === 7 ? "week" : `${dayCount}day`}`;
  const gridCols = `64px repeat(${dayCount}, 1fr)`;

  return (
    <div className="card-flat" data-testid={testId}>
      <CalendarHeader
        label={fmtRange(days)}
        onPrev={() => setCursor(addDays(cursor, -navStep))}
        onNext={() => setCursor(addDays(cursor, navStep))}
        onToday={() => setCursor(new Date())}
      />

      {/* Day header row */}
      <div className="grid border-b border-white/10" style={{ gridTemplateColumns: gridCols }}>
        <div />
        {days.map((d) => {
          const isToday = startOfDay(d).toISOString() === today;
          return (
            <div key={d.toISOString()} className={`px-3 py-3 text-center border-l border-white/10 ${isToday ? "bg-brand/10" : ""}`}>
              <div className="tracker text-zinc-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className={`font-display text-2xl ${isToday ? "text-brand-light" : ""}`}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* Away-client strip — one row per day, tucked under the day headers so
          the trainer immediately sees who's out before booking. */}
      {awayPeriods.length > 0 && (
        <div className="grid border-b border-white/10 bg-brand/5" style={{ gridTemplateColumns: gridCols }} data-testid="cal-away-strip-row">
          <div className="tracker text-[10px] text-brand-light/70 px-2 py-2 border-r border-white/10 flex items-center gap-1">
            <AirplaneTilt size={11} weight="duotone" /> AWAY
          </div>
          {days.map((d) => {
            const list = awayForDay(awayPeriods, d);
            const key = startOfDay(d).toISOString().slice(0, 10);
            return (
              <div
                key={key}
                className={`border-l border-white/10 px-1.5 py-1 flex flex-col gap-1 min-h-[26px] ${list.length ? "" : ""}`}
                data-testid={`cal-away-cell-${key}`}
              >
                {list.slice(0, 3).map((p) => (
                  <div
                    key={p.id}
                    className="tracker text-[10px] px-1.5 py-0.5 bg-brand/25 border border-brand/40 text-brand-light truncate"
                    title={`${p.client?.name || "Client"} — ${p.reason || "away"}`}
                    data-testid={`cal-away-pill-${p.id}-${key}`}
                  >
                    {p.client?.name || "Client"}
                  </div>
                ))}
                {list.length > 3 && (
                  <div className="tracker text-[9px] text-brand-light/70">+{list.length - 3}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Grid body */}
      <div className="relative grid" style={{ gridTemplateColumns: gridCols }}>
        {/* Hour gutter */}
        <div className="border-r border-white/10">
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_PX }} className="px-2 py-1 tracker text-[10px] text-zinc-600 text-right">
              {h === 12 ? "12 PM" : h < 12 ? `${h} AM` : `${h - 12} PM`}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map((d) => {
          const key = startOfDay(d).toISOString();
          const list = byDay[key] || [];
          return (
            <DayColumn
              key={d.toISOString()}
              day={d}
              hours={hours}
              list={list}
              active={active}
              setActive={setActive}
              onCreateAt={onCreateAt}
              onAction={onAction}
              onMove={onMove}
              onResize={onResize}
            />
          );
        })}

        {/* Now indicator */}
        <NowLine days={days} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// DayColumn — single day's vertical strip with click-and-drag slot selection
// ----------------------------------------------------------------
function DayColumn({ day, hours, list, active, setActive, onCreateAt, onAction, onMove, onResize }) {
  const colRef = useRef(null);
  const dragRef = useRef(null);
  const [dragState, setDragState] = useState(null);
  const totalSlots = hours.length * SLOTS_PER_HOUR;
  const SLOT_PX = HOUR_PX / SLOTS_PER_HOUR;
  const dayKey = day.toISOString().slice(0, 10);
  const slotFromEvent = (e) => {
    const rect = colRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    return Math.max(0, Math.min(totalSlots - 1, Math.floor(y / SLOT_PX)));
  };

  const slotToTime = (slotIdx) => {
    const totalMin = HOUR_START * 60 + slotIdx * SLOT_MIN;
    return `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;
  };

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('[data-testid^="week-session-"]')) return;
    const slot = slotFromEvent(e);
    dragRef.current = { startSlot: slot, endSlot: slot, dragged: false, pointerId: e.pointerId };
    setDragState({ startSlot: slot, endSlot: slot, dragged: false });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const slot = slotFromEvent(e);
    const next = { ...dragRef.current, endSlot: slot };
    if (slot !== dragRef.current.startSlot) next.dragged = true;
    dragRef.current = next;
    setDragState(next);
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragState(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_err) { /* ignore */ }

    const minSlot = Math.min(d.startSlot, d.endSlot);
    const maxSlot = Math.max(d.startSlot, d.endSlot);
    const startMin = HOUR_START * 60 + minSlot * SLOT_MIN;
    // Drag treats maxSlot as exclusive end: dragging from 14:00 down to the 15:30 slot ends at 15:30 (90 min)
    const endMin = HOUR_START * 60 + maxSlot * SLOT_MIN;
    const duration = d.dragged ? Math.max(SLOT_MIN, endMin - startMin) : null;

    const dt = new Date(day);
    dt.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
    onCreateAt?.(dt, duration);
  };

  const onPointerCancel = () => {
    dragRef.current = null;
    setDragState(null);
  };

  let preview = null;
  if (dragState && dragState.dragged) {
    const minSlot = Math.min(dragState.startSlot, dragState.endSlot);
    const maxSlot = Math.max(dragState.startSlot, dragState.endSlot);
    const top = minSlot * SLOT_PX;
    const height = (maxSlot - minSlot) * SLOT_PX;
    const dur = (maxSlot - minSlot) * SLOT_MIN;
    preview = { top, height, label: `${slotToTime(minSlot)} – ${slotToTime(maxSlot)} · ${dur}m` };
  }

  return (
    <div
      ref={colRef}
      className="relative border-l border-white/10 select-none touch-none cursor-crosshair"
      style={{ height: HOUR_PX * hours.length }}
      data-day-iso={day.toISOString()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      data-testid={`day-column-${dayKey}`}
    >
      {hours.map((h) => (
        <div
          key={`hr-${h}`}
          className="absolute left-0 right-0 border-b border-white/5 pointer-events-none"
          style={{ top: (h - HOUR_START) * HOUR_PX, height: HOUR_PX }}
        />
      ))}
      {hours.flatMap((h) =>
        Array.from({ length: SLOTS_PER_HOUR }, (_, q) => {
          const minute = q * SLOT_MIN;
          return (
            <div
              key={`${h}-${minute}`}
              className="absolute left-0 right-0 hover:bg-brand/20 transition-colors"
              style={{
                top: (h - HOUR_START) * HOUR_PX + (minute / 60) * HOUR_PX,
                height: SLOT_PX,
              }}
              data-testid={`day-slot-${dayKey}-${String(h).padStart(2, "0")}${String(minute).padStart(2, "0")}`}
            />
          );
        })
      )}

      {list.map((s) => <SessionBlock key={s.id} s={s} active={active} setActive={setActive} onAction={onAction} onMove={onMove} onResize={onResize} day={day} hours={hours} />)}

      {preview && (
        <div
          className="absolute left-1 right-1 bg-brand/40 border border-brand pointer-events-none z-20"
          style={{ top: preview.top + 1, height: Math.max(20, preview.height - 2) }}
          data-testid={`drag-preview-${dayKey}`}
        >
          <div className="text-[10px] font-mono px-1.5 py-0.5 text-white whitespace-nowrap">{preview.label}</div>
        </div>
      )}
    </div>
  );
}


function SessionBlock({ s, active, setActive, onAction, onMove, onResize, day, hours }) {
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const resizeRef = useRef(null);
  const [resizeState, setResizeState] = useState(null); // { newDur }

  const dt = new Date(s.start_time);
  const hourFloat = dt.getHours() + dt.getMinutes() / 60;
  if (hourFloat < HOUR_START || hourFloat >= HOUR_END) return null;
  const origDur = Math.max(15, Number(s.duration_minutes || 60));
  const dur = resizeState ? resizeState.newDur : origDur;
  const top = (hourFloat - HOUR_START) * HOUR_PX;
  const height = Math.max(28, (dur / 60) * HOUR_PX - 2);
  const styleClass = STATUS_STYLES[s.status] || STATUS_STYLES.scheduled;
  const open = active === s.id;
  const SLOT_PX = HOUR_PX / SLOTS_PER_HOUR;
  const totalSlots = hours.length * SLOTS_PER_HOUR;

  // Drag enabled for any session — trainer may want to move completed/cancelled too
  const canDrag = !s._group;
  // Resize allowed only when caller wires onResize (scheduled/upcoming sessions in TrainerSchedule)
  const canResize = !!onResize && !s._group;

  const slotToTime = (slotIdx) => {
    const totalMin = HOUR_START * 60 + slotIdx * SLOT_MIN;
    return `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;
  };

  const resolveDropTarget = (clientX, clientY) => {
    // Find the day-column under the cursor by scanning all columns' bounding rects.
    // More reliable than elementFromPoint when pointer is captured by the block.
    const cols = document.querySelectorAll("[data-day-iso]");
    let chosen = null;
    for (const c of cols) {
      const r = c.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) {
        chosen = { el: c, rect: r };
        break;
      }
    }
    if (!chosen) return null;
    const iso = chosen.el.getAttribute("data-day-iso");
    const y = clientY - chosen.rect.top - (dragRef.current?.grabOffsetY || 0);
    let slot = Math.round(y / SLOT_PX);
    slot = Math.max(0, Math.min(totalSlots - Math.ceil(dur / SLOT_MIN), slot));
    return { iso, slot, rect: chosen.rect };
  };

  const onPointerDown = (e) => {
    if (!canDrag || e.button !== 0) return;
    // Don't start a drag when the user is interacting with the popover that's
    // mounted inside the SessionBlock (Edit / Remind / Done / Cancel buttons),
    // nor when they grab the resize handle at the bottom edge.
    if (e.target.closest && e.target.closest('[data-testid^="session-popover-"]')) return;
    if (e.target.closest && e.target.closest('[data-resize-handle="1"]')) return;
    e.stopPropagation();
    const blockRect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      blockEl: e.currentTarget,
      startX: e.clientX,
      startY: e.clientY,
      blockW: blockRect.width,
      blockH: blockRect.height,
      grabOffsetY: e.clientY - blockRect.top,
      grabOffsetX: e.clientX - blockRect.left,
      moved: false,
      target: null,
      lastClientX: e.clientX,
      lastClientY: e.clientY,
    };
    setDrag({ target: null, ghostX: e.clientX, ghostY: e.clientY });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > 4) d.moved = true;
    d.lastClientX = e.clientX;
    d.lastClientY = e.clientY;
    const target = resolveDropTarget(e.clientX, e.clientY);
    d.target = target;
    setDrag({ target, ghostX: e.clientX - d.grabOffsetX, ghostY: e.clientY - d.grabOffsetY });
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDrag(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
    if (!d.moved) {
      setActive(open ? null : s.id);
      return;
    }
    const target = d.target || resolveDropTarget(d.lastClientX, d.lastClientY);
    if (!target) return;
    const totalMin = HOUR_START * 60 + target.slot * SLOT_MIN;
    const baseDay = new Date(target.iso);
    const newDt = new Date(baseDay);
    newDt.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
    onMove?.(s, newDt);
  };

  const onPointerCancel = () => { dragRef.current = null; setDrag(null); };

  // ---------- Resize logic (drag bottom handle to extend duration) ----------
  const onResizePointerDown = (e) => {
    if (!canResize || e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    resizeRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      origDur,
      moved: false,
      newDur: origDur,
    };
    setResizeState({ newDur: origDur });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
  };

  const onResizePointerMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    const deltaY = e.clientY - r.startY;
    if (!r.moved && Math.abs(deltaY) > 2) r.moved = true;
    const deltaSlots = Math.round(deltaY / SLOT_PX);
    let newDur = r.origDur + deltaSlots * SLOT_MIN;
    const startMin = (hourFloat - HOUR_START) * 60;
    const maxDur = hours.length * 60 - startMin;
    newDur = Math.max(SLOT_MIN, Math.min(maxDur, newDur));
    r.newDur = newDur;
    setResizeState({ newDur });
  };

  const onResizePointerUp = (e) => {
    const r = resizeRef.current;
    if (!r) { return; }
    resizeRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_err) { /* ignore */ }
    const finalDur = r.newDur ?? r.origDur;
    setResizeState(null);
    if (!r.moved || finalDur === r.origDur) return;
    onResize?.(s, finalDur);
  };

  const onResizePointerCancel = () => { resizeRef.current = null; setResizeState(null); };

  // While dragging, show original block dimmed in place; fixed ghost will be rendered separately
  const dragging = !!drag;
  const target = drag?.target;

  // Iter 59 — apply staff colour tint override on scheduled sessions
  const staffColorStyle = s._group
    ? { top: top + 1, height, borderLeftColor: "#8B5CF6", backgroundColor: "#8B5CF622", color: "#fff", borderLeftStyle: "dashed" }
    : (s.status === "scheduled" && s._staff_color)
    ? { top: top + 1, height, borderLeftColor: s._staff_color, backgroundColor: `${s._staff_color}22`, color: "#fff" }
    : { top: top + 1, height };

  return (
    <>
      <div
        className={`group absolute left-1 right-1 px-2 py-1 border-l-2 ${styleClass} z-10 ${canDrag ? "cursor-grab active:cursor-grabbing touch-none" : "cursor-pointer"} ${dragging ? "opacity-30" : "hover:brightness-110"}`}
        style={staffColorStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClick={!canDrag ? (e) => { if (e.target.closest?.("[data-testid^='group-popover-']")) return; e.stopPropagation(); setActive(open ? null : s.id); } : undefined}
        title={canDrag ? "Drag to reschedule" : undefined}
        data-testid={`week-session-${s.id}`}
      >
        <div className="text-[10px] font-mono leading-tight flex items-center gap-1">
          {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          {s.series_id && <Repeat size={9} weight="bold" className="opacity-70" data-testid={`recurring-${s.id}`} />}
        </div>
        <div className="text-xs font-semibold truncate">{s.client?.name || "—"}</div>
        <div className="text-[10px] tracker truncate opacity-80">{s.session_type}</div>

        {canResize && !dragging && !resizeState && (() => {
          const startMin = (hourFloat - HOUR_START) * 60;
          const headroom = hours.length * 60 - startMin - dur;
          if (headroom < 15) return null;
          const extend = (mins, e) => {
            e.stopPropagation();
            e.preventDefault();
            const next = Math.min(dur + mins, hours.length * 60 - startMin);
            if (next === dur) return;
            onResize?.(s, next);
          };
          const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
          return (
            <div
              className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
              onPointerDown={stop}
              data-testid={`quick-extend-${s.id}`}
            >
              <button
                type="button"
                onPointerDown={stop}
                onClick={(e) => extend(15, e)}
                className="text-[9px] font-mono px-1 py-0.5 bg-black/60 border border-white/20 hover:border-brand-light hover:bg-brand/30 rounded"
                data-testid={`quick-extend-15-${s.id}`}
                title="Extend by 15 minutes"
              >
                +15
              </button>
              {headroom >= 30 && (
                <button
                  type="button"
                  onPointerDown={stop}
                  onClick={(e) => extend(30, e)}
                  className="text-[9px] font-mono px-1 py-0.5 bg-black/60 border border-white/20 hover:border-brand-light hover:bg-brand/30 rounded"
                  data-testid={`quick-extend-30-${s.id}`}
                  title="Extend by 30 minutes"
                >
                  +30
                </button>
              )}
            </div>
          );
        })()}

        {open && !dragging && (
          <SessionPopover s={s} onAction={onAction} close={() => setActive(null)} />
        )}

        {canResize && (
          <div
            data-resize-handle="1"
            data-testid={`resize-handle-${s.id}`}
            onPointerDown={onResizePointerDown}
            onPointerMove={onResizePointerMove}
            onPointerUp={onResizePointerUp}
            onPointerCancel={onResizePointerCancel}
            className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize touch-none flex items-end justify-center hover:bg-brand/30 transition-colors"
            title="Drag to change duration"
          >
            <div className="h-[3px] w-8 bg-white/30 rounded-full mb-[2px]" />
          </div>
        )}

        {resizeState && (
          <div
            className="absolute -right-1 top-1 bg-brand text-white text-[10px] font-mono px-1.5 py-0.5 z-20 pointer-events-none"
            data-testid={`resize-preview-${s.id}`}
          >
            {resizeState.newDur}m
          </div>
        )}
      </div>

      {dragging && target && (() => {
        const ghostTime = slotToTime(target.slot);
        const targetDate = new Date(target.iso);
        const dayLabel = targetDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        // Position ghost over the target column at the snapped slot
        const ghostTop = target.rect.top + target.slot * SLOT_PX + window.scrollY;
        const ghostLeft = target.rect.left + 4 + window.scrollX;
        const ghostWidth = target.rect.width - 8;
        return (
          <div
            className={`fixed pointer-events-none z-50 px-2 py-1 border-l-2 ${styleClass} ring-2 ring-brand shadow-2xl`}
            style={{
              top: ghostTop - window.scrollY,
              left: ghostLeft - window.scrollX,
              width: ghostWidth,
              height,
            }}
            data-testid={`drag-ghost-${s.id}`}
          >
            <div className="text-[10px] font-mono leading-tight">{ghostTime}</div>
            <div className="text-xs font-semibold truncate">{s.client?.name || "—"}</div>
            <div className="text-[10px] tracker truncate opacity-80">{dayLabel}</div>
          </div>
        );
      })()}
    </>
  );
}

function SessionPopover({ s, onAction, close }) {
  const ref = useRef(null);
  const trainerTz = useTrainerTimezone();
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    setTimeout(() => document.addEventListener("click", fn), 0);
    return () => document.removeEventListener("click", fn);
  }, [close]);
  const isScheduled = s.status === "scheduled";
  const clientTz = s.client?.timezone;
  const showGhost = clientTz && clientTz !== trainerTz;
  if (s._group) {
    const c = s._cls || {};
    return (
      <div ref={ref} className="absolute top-full left-0 mt-1 w-64 card-flat p-3 z-30 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={`group-popover-${c.id}`}>
        <div className="tracker text-[9px] text-brand-light mb-1">GROUP CLASS</div>
        <div className="text-xs font-semibold">{c.title}</div>
        <div className="text-[10px] tracker text-zinc-500 mb-1">
          {c.duration_min}m{c.location ? ` · ${c.location}` : ""}{c.instructor_name ? ` · with ${c.instructor_name}` : " · no instructor"}
        </div>
        <div className="text-[10px] text-zinc-300 mb-2" data-testid={`group-popover-stats-${c.id}`}>
          {c.booked_count}/{c.capacity} booked{c.waitlist_count ? ` · ${c.waitlist_count} waiting` : ""}
          {(c.attended_count || c.no_show_count) ? ` · ${c.attended_count || 0} attended · ${c.no_show_count || 0} no-show` : ""}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onAction?.("group", s); close(); }} className="btn-primary !py-1 !px-2 !text-[10px] w-full justify-center" data-testid={`pop-group-open-${c.id}`}>
          Attendees & check-in
        </button>
      </div>
    );
  }
  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-1 w-60 card-flat p-3 z-30 shadow-xl"
      onClick={(e) => e.stopPropagation()}
      data-testid={`session-popover-${s.id}`}
    >
      <div className="text-xs font-semibold">{s.client?.name}</div>
      <div className="text-[10px] tracker text-zinc-500 mb-1">{s.session_type} · {s.duration_minutes}m · <span className="money">${(s.price || 0).toFixed(2)}</span></div>
      {showGhost && (
        <div className="flex items-center gap-1.5 text-[10px] text-brand-light/90 font-mono mb-2" title={`Client is in ${clientTz}`}>
          <GlobeHemisphereEast size={11} weight="duotone" />
          {formatTimeInTz(s.start_time, clientTz)} {shortTzLabel(clientTz)} their time
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {isScheduled && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onAction?.("edit", s); close(); }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`pop-edit-${s.id}`}>
              <PencilSimple size={12} weight="bold" /> Edit
            </button>
            <button onClick={(e) => { e.stopPropagation(); onAction?.("remind", s); close(); }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`pop-remind-${s.id}`}>
              <Bell size={12} weight="bold" /> Remind
            </button>
            <button onClick={(e) => { e.stopPropagation(); onAction?.("complete", s); close(); }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`pop-complete-${s.id}`}>
              <CheckCircle size={12} weight="bold" /> Done
            </button>
            <button onClick={(e) => { e.stopPropagation(); onAction?.("cancel", s); close(); }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`pop-cancel-${s.id}`}>
              <X size={12} weight="bold" /> Cancel
            </button>
          </>
        )}
        <button onClick={(e) => { e.stopPropagation(); onAction?.("delete", s); close(); }} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`pop-delete-${s.id}`}>
          <Trash size={12} weight="bold" /> Delete
        </button>
      </div>
    </div>
  );
}

function NowLine({ days }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const now = new Date();
  if (!days.length) return null;
  const stripStart = days[0];
  const stripEnd = addDays(days[days.length - 1], 1);
  if (now < stripStart || now >= stripEnd) return null;
  const dayIdx = Math.floor((startOfDay(now) - stripStart) / (1000 * 60 * 60 * 24));
  const hourFloat = now.getHours() + now.getMinutes() / 60;
  if (hourFloat < HOUR_START || hourFloat >= HOUR_END) return null;
  const top = (hourFloat - HOUR_START) * HOUR_PX;
  const n = days.length;
  return (
    <div
      className="absolute pointer-events-none z-20"
      style={{
        top,
        left: `calc(64px + ${dayIdx} * (100% - 64px) / ${n})`,
        width: `calc((100% - 64px) / ${n})`,
      }}
    >
      <div className="relative">
        <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-[#FF3B30]" />
        <div className="h-px bg-[#FF3B30]" />
      </div>
    </div>
  );
}

// ---------------- Month View ----------------
function MonthView({ cursor, setCursor, sessions, awayPeriods = [], onCreateAt, onAction }) {
  const monthStart = useMemo(() => startOfMonth(cursor), [cursor]);
  const gridStart = useMemo(() => startOfWeek(monthStart), [monthStart]);
  const cells = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);

  const byDay = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      const key = startOfDay(new Date(s.start_time)).toISOString();
      (map[key] ||= []).push(s);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [sessions]);

  const today = startOfDay(new Date()).toISOString();
  const monthIdx = monthStart.getMonth();

  return (
    <div className="card-flat" data-testid="calendar-month">
      <CalendarHeader
        label={fmtMonth(monthStart)}
        onPrev={() => { const x = new Date(monthStart); x.setMonth(x.getMonth() - 1); setCursor(x); }}
        onNext={() => { const x = new Date(monthStart); x.setMonth(x.getMonth() + 1); setCursor(x); }}
        onToday={() => setCursor(new Date())}
      />

      <div className="grid grid-cols-7 border-b border-white/10">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
          <div key={d} className="tracker text-zinc-500 text-center py-3 border-l border-white/10 first:border-l-0">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(110px, 1fr)" }}>
        {cells.map((d, i) => {
          const key = startOfDay(d).toISOString();
          const isToday = key === today;
          const dim = d.getMonth() !== monthIdx;
          const list = byDay[key] || [];
          return (
            <div
              key={i}
              className={`border-l border-t border-white/10 first:border-l-0 p-2 ${dim ? "bg-black/40" : ""} ${isToday ? "ring-1 ring-inset ring-brand/40" : ""} hover:bg-[#1A1A1A]/40 cursor-pointer`}
              onClick={() => {
                const dt = new Date(d);
                dt.setHours(9, 0, 0, 0);
                onCreateAt?.(dt);
              }}
              data-testid={`month-day-${key.slice(0, 10)}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`font-mono text-xs ${dim ? "text-zinc-700" : isToday ? "text-brand-light font-bold" : "text-zinc-300"}`}>
                  {d.getDate()}
                </span>
                {list.length > 0 && (
                  <span className="tracker text-[9px] text-zinc-500">{list.length}</span>
                )}
              </div>
              {(() => {
                const away = awayForDay(awayPeriods, d);
                if (away.length === 0) return null;
                return (
                  <div className="mb-1 space-y-0.5" data-testid={`month-away-${key.slice(0, 10)}`}>
                    {away.slice(0, 2).map((p) => (
                      <div
                        key={p.id}
                        className={`px-1 py-0.5 text-[9px] bg-brand/20 border-l-2 border-brand text-[#A8B9DC] truncate flex items-center gap-0.5 ${dim ? "opacity-40" : ""}`}
                        title={`${p.client?.name || "Client"} away — ${p.reason || "no reason"}`}
                      >
                        <AirplaneTilt size={9} weight="bold" className="shrink-0" />
                        <span className="truncate">{p.client?.name || "Client"}</span>
                      </div>
                    ))}
                    {away.length > 2 && (
                      <div className="tracker text-[8px] text-brand-light/70 pl-1">+{away.length - 2} away</div>
                    )}
                  </div>
                );
              })()}
              <div className="space-y-1">
                {list.slice(0, 3).map((s) => {
                  const dt = new Date(s.start_time);
                  const style = STATUS_STYLES[s.status] || STATUS_STYLES.scheduled;
                  const monthTint = s._group
                    ? { borderLeftColor: "#8B5CF6", backgroundColor: "#8B5CF633", color: "#fff", borderLeftStyle: "dashed" }
                    : (s.status === "scheduled" && s._staff_color)
                    ? { borderLeftColor: s._staff_color, backgroundColor: `${s._staff_color}33`, color: "#fff" }
                    : undefined;
                  return (
                    <div
                      key={s.id}
                      className={`px-1.5 py-0.5 text-[10px] border-l-2 truncate ${style}`}
                      style={monthTint}
                      onClick={(e) => { e.stopPropagation(); onAction?.("open", s); }}
                      data-testid={`month-session-${s.id}`}
                      title={`${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${s.client?.name} · ${s.session_type}`}
                    >
                      <span className="font-mono">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>{" "}
                      <span className="font-semibold">{s.client?.name || "—"}</span>
                    </div>
                  );
                })}
                {list.length > 3 && (
                  <div className="tracker text-[9px] text-zinc-500 pl-1">+{list.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- Shared Header ----------------
function CalendarHeader({ label, onPrev, onNext, onToday }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
      <div className="flex items-center gap-1">
        <button onClick={onPrev} className="btn-ghost !py-1.5 !px-2" data-testid="cal-prev"><CaretLeft size={14} weight="bold" /></button>
        <button onClick={onToday} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="cal-today">Today</button>
        <button onClick={onNext} className="btn-ghost !py-1.5 !px-2" data-testid="cal-next"><CaretRight size={14} weight="bold" /></button>
      </div>
      <div className="font-display text-2xl" data-testid="cal-label">{label}</div>
      <div className="w-[120px]" />
    </div>
  );
}
